import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const images = [
  process.env.PUBLIC_URL + '/images/Booking1.jpg',
  process.env.PUBLIC_URL + '/images/Booking2.jpg',
  process.env.PUBLIC_URL + '/images/Booking3.jpg',
  process.env.PUBLIC_URL + '/images/Booking4.jpg',
];

const roomOptions = [
  { type: "Single", price: "Rs. 1000 per night" },
  { type: "Double", price: "Rs. 2500 per night" },
  { type: "Suite", price: "Rs. 5000 per night" },
  { type: "Penthouse", price: "Rs. 10000 per night" },
];

function Booking() {
  const navigate = useNavigate();
  const currentUser = localStorage.getItem('currentUser') || '';

  const [currentImage, setCurrentImage] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: currentUser,
    checkin: '',
    checkout: '',
    roomType: '',
  });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);

  const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    document.title = "Hotel Atlanta | Book Now";
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const closeOnClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOnClickOutside);
    return () => document.removeEventListener("mousedown", closeOnClickOutside);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const handleRoomSelect = (type) => {
    setFormData({ ...formData, roomType: type });
    setDropdownOpen(false);
    setHighlightedIndex(-1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const roomPrices = {
      Single: "Rs. 1000 per night",
      Double: "Rs. 2500 per night",
      Suite: "Rs. 5000 per night",
      Penthouse: "Rs. 10000 per night",
    };
    const fullBooking = {
      ...formData,
      roomLabel: `${formData.roomType} — ${roomPrices[formData.roomType]}`,
      payment: "Payment on arrival",
    };
    const bookings = JSON.parse(localStorage.getItem('hotelBookings')) || [];
    bookings.push(fullBooking);
    localStorage.setItem('hotelBookings', JSON.stringify(bookings));
    alert("Booking submitted!");
    setFormData({
      name: '',
      email: currentUser,
      checkin: '',
      checkout: '',
      roomType: '',
    });
  };

  return (
    <div className="booking-wrapper">
      <div className="booking-background">
        {images.map((src, index) => (
          <img
            key={index}
            src={src}
            className={`fade-image ${currentImage === index ? 'show' : ''}`}
            alt={`Slide ${index + 1}`}
          />
        ))}
      </div>

      <div className="booking-overlay">
        <h1 style={{ textAlign: 'center', marginBottom: '30px' }}>Book Your Stay</h1>

        {isLoggedIn ? (
          <form onSubmit={handleSubmit}>
            {/* Name (editable) */}
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="name" style={{ display: 'block', marginBottom: '8px' }}>Name</label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  backgroundColor: '#fff',
                  cursor: 'text',
                }}
              />
            </div>

            {/* Email (read-only, full) */}
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="email" style={{ display: 'block', marginBottom: '8px' }}>Email</label>
              <input
                type="email"
                id="email"
                value={formData.email}
                readOnly
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  backgroundColor: '#eee',
                  cursor: 'not-allowed',
                }}
              />
            </div>

            {/* Dates */}
            {[
              { id: 'checkin', label: 'Check-In Date', type: 'date', min: today },
              { id: 'checkout', label: 'Check-Out Date', type: 'date', min: formData.checkin || today },
            ].map(({ id, label, type, min }) => (
              <div key={id} style={{ marginBottom: '20px' }}>
                <label htmlFor={id} style={{ display: 'block', marginBottom: '8px' }}>{label}</label>
                <input
                  type={type}
                  id={id}
                  value={formData[id]}
                  onChange={handleChange}
                  required
                  min={min}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                  }}
                />
              </div>
            ))}

            {/* Custom Dropdown */}
            <div style={{ marginBottom: '20px', position: 'relative' }} ref={dropdownRef}>
              <label htmlFor="roomType" style={{ display: 'block', marginBottom: '8px' }}>Room Type</label>
              <div
                tabIndex={0}
                onClick={() => setDropdownOpen(!dropdownOpen)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setDropdownOpen(!dropdownOpen);
                  } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setDropdownOpen(true);
                    setHighlightedIndex((prev) => (prev + 1) % roomOptions.length);
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setDropdownOpen(true);
                    setHighlightedIndex((prev) => (prev - 1 + roomOptions.length) % roomOptions.length);
                  } else if (e.key === 'Enter' && dropdownOpen && highlightedIndex >= 0) {
                    handleRoomSelect(roomOptions[highlightedIndex].type);
                  }
                }}
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={dropdownOpen}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  backgroundColor: '#fff',
                  color: '#000',
                  cursor: 'pointer',
                  height: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  fontFamily: 'monospace',
                }}
              >
                {formData.roomType
                  ? `${formData.roomType} — ${roomOptions.find(r => r.type === formData.roomType)?.price}`
                  : 'Select Room Type'}
              </div>

              {dropdownOpen && (
                <div
                  role="listbox"
                  className="fade-in"
                  style={{
                    position: 'absolute',
                    background: '#fff',
                    width: '100%',
                    border: '1px solid #ccc',
                    borderTop: 'none',
                    zIndex: 20,
                    maxHeight: '160px',
                    overflowY: 'auto',
                    boxShadow: '0px 4px 10px rgba(0,0,0,0.1)',
                    transition: 'opacity 0.3s ease',
                  }}
                >
                  {roomOptions.map(({ type, price }, index) => (
                    <div
                      key={type}
                      role="option"
                      aria-selected={formData.roomType === type}
                      onClick={() => handleRoomSelect(type)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      style={{
                        padding: '10px 15px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid #eee',
                        cursor: 'pointer',
                        backgroundColor:
                          highlightedIndex === index
                            ? '#eaeaea'
                            : formData.roomType === type
                            ? '#f1f1f1'
                            : '#fff',
                        fontFamily: 'monospace',
                        color: '#000',
                      }}
                    >
                      <span>{type}</span>
                      <span style={{ marginLeft: '20px' }}>{price}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p style={{ fontStyle: 'italic', marginBottom: '15px' }}>
              Payment to be made on arrival.
            </p>

            <button type="submit" className="btn" style={{ width: '100%' }}>
              Submit Booking
            </button>
          </form>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <p style={{ marginBottom: '20px' }}>You must be logged in to make a booking.</p>
            <button className="btn" onClick={() => navigate('/login')} style={{ marginRight: '10px' }}>
              Login
            </button>
            <button className="btn btn-light" onClick={() => navigate('/signup')}>
              Sign Up
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Booking;